export type CalendarViewMode = 'single' | 'overlay' | 'multi-column';

interface ViewModeToggleProps {
  viewMode: CalendarViewMode;
  onViewModeChange: (mode: CalendarViewMode) => void;
  multiStaffEnabled: boolean;
  disabled: boolean;
}

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const LayersIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

const ColumnsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >
    <rect x="3" y="3" width="7" height="18" />
    <rect x="14" y="3" width="7" height="18" />
  </svg>
);

export default function ViewModeToggle({
  viewMode,
  onViewModeChange,
  multiStaffEnabled,
  disabled,
}: ViewModeToggleProps) {
  const modes: Array<{
    id: CalendarViewMode;
    label: string;
    icon: React.ReactNode;
    requiresMultiStaff: boolean;
  }> = [
    {
      id: 'single',
      label: 'Day',
      icon: <CalendarIcon />,
      requiresMultiStaff: false,
    },
    {
      id: 'overlay',
      label: 'Overlay',
      icon: <LayersIcon />,
      requiresMultiStaff: true,
    },
    {
      id: 'multi-column',
      label: 'Multi-Column',
      icon: <ColumnsIcon />,
      requiresMultiStaff: true,
    },
  ];

  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white shadow-sm">
      {modes.map((mode, index) => {
        const isActive = viewMode === mode.id;
        const isDisabled =
          disabled || (mode.requiresMultiStaff && !multiStaffEnabled);
        const isFirst = index === 0;
        const isLast = index === modes.length - 1;

        return (
          <button
            key={mode.id}
            type="button"
            onClick={() => !isDisabled && onViewModeChange(mode.id)}
            disabled={isDisabled}
            className={`
              relative inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
              transition-colors duration-150
              ${isFirst ? 'rounded-l-lg' : ''}
              ${isLast ? 'rounded-r-lg' : ''}
              ${!isFirst ? 'border-l border-gray-300' : ''}
              ${
                isActive
                  ? 'bg-blue-600 text-white z-10'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }
              ${
                isDisabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            `}
            title={
              mode.requiresMultiStaff && !multiStaffEnabled
                ? 'Select staff first'
                : mode.label
            }
          >
            {mode.icon}
            <span>{mode.label}</span>
          </button>
        );
      })}
    </div>
  );
}
